import { useCallback, useEffect, useRef, useState } from "react";
import { listActivities, resumeActivity } from "../services/api";
import { logConnectorFailure, logConnectorSuccess } from "../../telemetry";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Activity, Decision } from "../types";

/** How often the panel re-polls for pending decisions while it is open. */
export const POLL_INTERVAL_MS = 15_000;

/** How long a just-resolved activity stays suppressed from poll results.
 *  `POST /resume` only enqueues the resume; the record stays `awaiting_input`
 *  until the poller claims it, so the server keeps returning it for a short
 *  window. We hide it locally until then — but bounded, so a resume that never
 *  gets claimed (e.g. enqueue lost) eventually re-surfaces for another try. */
export const RESOLVED_TTL_MS = 60_000;

export interface UseActivities {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  /** The id of the activity currently being submitted, or null. */
  submittingId: string | null;
  refresh: () => void;
  submit: (activity: Activity, decision: Decision) => Promise<void>;
}

/** Epoch ms for an activity's creation time; NaN when absent or unparseable. */
function createdAtMs(activity: Activity): number {
  return Date.parse(activity.created_at);
}

/**
 * Newest-first display order (AIVA-7905).
 *
 * `GET /activities` runs `SELECT * FROM c WHERE c.status = @status` with no
 * ORDER BY, so Cosmos returns rows in index order — in practice oldest-first —
 * and the panel rendered that verbatim. Sorting here rather than in the Cosmos
 * query keeps the fix independent of the HITLRecords indexing policy (the
 * container is provisioned by aiva-deploy's Terraform, so a cross-partition
 * ORDER BY on an unindexed `/created_at` would fail the whole request instead
 * of merely mis-ordering it), and this hook already owns the displayed list —
 * it filters just-resolved ids out of every poll result.
 *
 * Rows with no usable `created_at` sort last, and ties break on `id`, so the
 * order is a total one and the list cannot jitter between polls.
 */
export function sortNewestFirst(activities: Activity[]): Activity[] {
  return [...activities].sort((a, b) => {
    const aMs = createdAtMs(a);
    const bMs = createdAtMs(b);
    const aUndated = Number.isNaN(aMs);
    const bUndated = Number.isNaN(bMs);
    if (aUndated !== bUndated) return aUndated ? 1 : -1;
    if (!aUndated && aMs !== bMs) return bMs - aMs;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Loads and polls pending HITL decisions, and submits the user's answer.
 *
 * Polling (not SSE) because background runs are detached — there is no live
 * connection to stream against. The token is read through a ref so the poll
 * interval doesn't restart on every token refresh.
 */
export function useActivities(
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
): UseActivities {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  // Read token + refresh through refs so the poll interval doesn't restart on
  // every token refresh.
  const tokenRef = useRef(apiToken);
  tokenRef.current = apiToken;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;

  // ids of activities the user just resolved, mapped to when they were resolved.
  // Filtered out of poll results until the server stops returning them or the
  // TTL lapses — see RESOLVED_TTL_MS.
  const resolvedRef = useRef<Map<string, number>>(new Map());

  const load = useCallback(async () => {
    try {
      setError(null);
      const fetched = await listActivities(tokenRef.current, refreshRef.current);
      const now = Date.now();
      const resolved = resolvedRef.current;
      // Drop entries past their TTL so a never-claimed resume re-surfaces.
      for (const [id, at] of resolved) {
        if (now - at > RESOLVED_TTL_MS) resolved.delete(id);
      }
      const visible = fetched.filter((a) => !resolved.has(a.id));
      // Once the server stops advertising an id, the resume took — forget it so
      // a future interrupt reusing the id isn't wrongly suppressed.
      const stillPending = new Set(fetched.map((a) => a.id));
      for (const id of resolved.keys()) {
        if (!stillPending.has(id)) resolved.delete(id);
      }
      setActivities(sortNewestFirst(visible));
      logConnectorSuccess("approvals", "load-activities");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load approvals");
      logConnectorFailure("approvals", e, "load-activities");
    } finally {
      setLoading(false);
    }
  }, []);

  // load() swallows its own errors, so the floating promise never rejects.
  const refresh = useCallback(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const submit = useCallback(async (activity: Activity, decision: Decision) => {
    setSubmittingId(activity.id);
    try {
      await resumeActivity(tokenRef.current, activity, decision, refreshRef.current);
      // Suppress this id from subsequent polls until the server stops returning
      // it (the resume only enqueues; the record lingers as awaiting_input).
      resolvedRef.current.set(activity.id, Date.now());
      // Optimistically drop the resolved item; the next poll reconciles.
      setActivities((prev) => prev.filter((a) => a.id !== activity.id));
      logConnectorSuccess("approvals", "submit-decision");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to submit decision");
      logConnectorFailure("approvals", e, "submit-decision");
      throw e;
    } finally {
      setSubmittingId(null);
    }
  }, []);

  return {
    activities,
    loading,
    error,
    submittingId,
    refresh,
    submit,
  };
}
